import { apiFetch } from '../../utils/apiFetch';
import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';

interface SystemHealth {
  system_status: string;
  active_alerts: Array<{
    id: number;
    severity: string;
    title: string;
    message: string;
    created_at: string;
    duration: number;
    is_overdue: boolean;
  }>;
  alert_counts: {
    critical: number;
    warning: number;
    info: number;
  };
  last_check: string;
}

interface SystemMetrics {
  tenant_metrics: {
    total_tenants: number;
    active_tenants: number;
    trial_tenants: number;
    paid_tenants: number;
    new_tenants_this_month: number;
  };
  performance_metrics: {
    avg_response_time: number;
    error_rate: number;
    uptime_percentage: number;
    requests_per_minute: number;
  };
  activity_metrics: {
    analyses_today: number;
    aio_analyses_today: number;
    websites_analyzed_today: number;
    active_users_today: number;
  };
}

const SystemOverview: React.FC = () => {
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null);
  const [systemMetrics, setSystemMetrics] = useState<SystemMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  useEffect(() => {
    fetchSystemOverview();
    const interval = setInterval(fetchSystemOverview, 30000); // Update every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchSystemOverview = async () => {
    try {
      const response = await apiFetch('/api/admin/dashboard');
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      setSystemHealth(data.system_health);
      setSystemMetrics({
        tenant_metrics: data.tenant_metrics,
        performance_metrics: data.performance_metrics,
        activity_metrics: data.activity_metrics,
      });
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Failed to fetch system overview:', error);
      toast.error('Failed to load system overview');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'operational':
      case 'healthy':
        return 'text-emerald-400 bg-emerald-500/20 border-emerald-500/30';
      case 'degraded':
        return 'text-amber-400 bg-amber-500/20 border-amber-500/30';
      case 'outage':
      case 'unhealthy':
        return 'text-red-400 bg-red-500/20 border-red-500/30';
      default:
        return 'text-slate-400 bg-slate-500/20 border-slate-500/30';
    }
  };

  const getScoreColor = (score: number): string => {
    if (score >= 90) return 'text-emerald-400';
    if (score >= 70) return 'text-amber-400';
    return 'text-red-400';
  };

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-32 bg-slate-700/50 rounded-xl border border-slate-600/50"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 bg-slate-700/50 rounded-xl border border-slate-600/50"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">System Overview</h2>
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
          <div className="text-sm text-slate-400">
            Last updated: {lastUpdated.toLocaleTimeString()}
          </div>
        </div>
      </div>

      {/* System Status Card */}
      {systemHealth && (
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50 shadow-xl">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-white">System Status</h3>
            <span className={`px-4 py-2 rounded-lg text-sm font-medium border ${getStatusColor(systemHealth.system_status)}`}>
              {systemHealth.system_status.charAt(0).toUpperCase() + systemHealth.system_status.slice(1)}
            </span>
          </div>
          
          {/* Alert Summary */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
              <div className="text-3xl font-bold text-red-400">{systemHealth.alert_counts.critical}</div>
              <div className="text-sm text-red-300 mt-1">Critical Alerts</div>
            </div>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
              <div className="text-3xl font-bold text-amber-400">{systemHealth.alert_counts.warning}</div>
              <div className="text-sm text-amber-300 mt-1">Warnings</div>
            </div>
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
              <div className="text-3xl font-bold text-blue-400">{systemHealth.alert_counts.info}</div>
              <div className="text-sm text-blue-300 mt-1">Info</div>
            </div>
          </div>

          {/* Active Alerts List */}
          {systemHealth.active_alerts.length > 0 && (
            <div className="mt-6 space-y-3">
              <h4 className="text-sm font-medium text-slate-400 mb-3">Active Alerts</h4>
              {systemHealth.active_alerts.map((alert) => (
                <div 
                  key={alert.id} 
                  className={`p-3 rounded-lg border ${
                    alert.severity === 'critical' ? 'bg-red-500/10 border-red-500/30' :
                    alert.severity === 'warning' ? 'bg-amber-500/10 border-amber-500/30' :
                    'bg-blue-500/10 border-blue-500/30'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h5 className={`font-medium ${
                        alert.severity === 'critical' ? 'text-red-300' :
                        alert.severity === 'warning' ? 'text-amber-300' :
                        'text-blue-300'
                      }`}>{alert.title}</h5>
                      <p className="text-sm text-slate-400 mt-1 line-clamp-2">{alert.message}</p>
                    </div>
                    <span className={`ml-4 px-2 py-1 text-xs font-medium rounded ${
                      alert.severity === 'critical' ? 'bg-red-500/20 text-red-400' :
                      alert.severity === 'warning' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-blue-500/20 text-blue-400'
                    }`}>
                      {alert.severity}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Performance metrics section - commented out as these fields don't exist yet
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center bg-slate-700/30 rounded-xl p-4 border border-slate-600/30">
              <div className={`text-3xl font-bold mb-2 ${getScoreColor(systemHealth.performance_score)}`}>
                {systemHealth.performance_score}%
              </div>
              <div className="text-sm text-slate-400">Performance Score</div>
              <div className="w-full bg-slate-600 rounded-full h-2 mt-3">
                <div 
                  className={`h-2 rounded-full transition-all duration-500 ${
                    systemHealth.performance_score >= 90 ? 'bg-gradient-to-r from-emerald-500 to-emerald-400' :
                    systemHealth.performance_score >= 70 ? 'bg-gradient-to-r from-amber-500 to-amber-400' : 
                    'bg-gradient-to-r from-red-500 to-red-400'
                  }`}
                  style={{width: `${systemHealth.performance_score}%`}}
                ></div>
              </div>
            </div>
            <div className="text-center bg-slate-700/30 rounded-xl p-4 border border-slate-600/30">
              <div className={`text-3xl font-bold mb-2 ${getScoreColor(systemHealth.uptime_percentage)}`}>
                {systemHealth.uptime_percentage}%
              </div>
              <div className="text-sm text-slate-400">Uptime</div>
              <div className="w-full bg-slate-600 rounded-full h-2 mt-3">
                <div 
                  className={`h-2 rounded-full transition-all duration-500 ${
                    systemHealth.uptime_percentage >= 90 ? 'bg-gradient-to-r from-emerald-500 to-emerald-400' :
                    systemHealth.uptime_percentage >= 70 ? 'bg-gradient-to-r from-amber-500 to-amber-400' : 
                    'bg-gradient-to-r from-red-500 to-red-400'
                  }`}
                  style={{width: `${systemHealth.uptime_percentage}%`}}
                ></div>
              </div>
            </div>
            <div className="text-center bg-slate-700/30 rounded-xl p-4 border border-slate-600/30">
              <div className={`text-3xl font-bold mb-2 ${getScoreColor(systemHealth.health_score)}`}>
                {systemHealth.health_score}%
              </div>
              <div className="text-sm text-slate-400">Health Score</div>
              <div className="w-full bg-slate-600 rounded-full h-2 mt-3">
                <div 
                  className={`h-2 rounded-full transition-all duration-500 ${
                    systemHealth.health_score >= 90 ? 'bg-gradient-to-r from-emerald-500 to-emerald-400' :
                    systemHealth.health_score >= 70 ? 'bg-gradient-to-r from-amber-500 to-amber-400' : 
                    'bg-gradient-to-r from-red-500 to-red-400'
                  }`}
                  style={{width: `${systemHealth.health_score}%`}}
                ></div>
              </div>
            </div>
          </div>
          */}
        </div>
      )}

      {/* Metrics Grid */}
      {systemMetrics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Tenant Metrics */}
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50 shadow-xl hover:shadow-2xl hover:shadow-indigo-500/10 transition-all duration-300 group">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-500/20 to-purple-500/20 border border-indigo-500/30 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                  <svg className="w-6 h-6 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-white mb-1">Tenants</h3>
                <div className="text-3xl font-bold text-indigo-400 mb-1">
                  {systemMetrics.tenant_metrics.total_tenants.toLocaleString()}
                </div>
                <div className="text-sm text-slate-400">
                  {systemMetrics.tenant_metrics.active_tenants} active
                </div>
              </div>
            </div>
            <div className="mt-6 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Paid</span>
                <span className="font-medium text-slate-200">{systemMetrics.tenant_metrics.paid_tenants}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Trial</span>
                <span className="font-medium text-slate-200">{systemMetrics.tenant_metrics.trial_tenants}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">New this month</span>
                <span className="font-medium text-emerald-400 flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11l5-5m0 0l5 5m-5-5v12" />
                  </svg>
                  +{systemMetrics.tenant_metrics.new_tenants_this_month}
                </span>
              </div>
            </div>
          </div>

          {/* Performance Metrics */}
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50 shadow-xl hover:shadow-2xl hover:shadow-emerald-500/10 transition-all duration-300 group">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-500/20 to-green-500/20 border border-emerald-500/30 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                  <svg className="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-white mb-1">Performance</h3>
                <div className="text-3xl font-bold text-emerald-400 mb-1">
                  {systemMetrics.performance_metrics.avg_response_time}ms
                </div>
                <div className="text-sm text-slate-400">avg response time</div>
              </div>
            </div>
            <div className="mt-6 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Error rate</span>
                <span className={`font-medium ${systemMetrics.performance_metrics.error_rate > 1 ? 'text-red-400' : 'text-slate-200'}`}>
                  {systemMetrics.performance_metrics.error_rate}%
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Requests/min</span>
                <span className="font-medium text-slate-200">{systemMetrics.performance_metrics.requests_per_minute.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Uptime</span>
                <span className="font-medium text-emerald-400">{systemMetrics.performance_metrics.uptime_percentage}%</span>
              </div>
            </div>
          </div>

          {/* Activity Metrics */}
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50 shadow-xl hover:shadow-2xl hover:shadow-purple-500/10 transition-all duration-300 group">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500/20 to-violet-500/20 border border-purple-500/30 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                  <svg className="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-white mb-1">Today's Activity</h3>
                <div className="text-3xl font-bold text-purple-400 mb-1">
                  {systemMetrics.activity_metrics.analyses_today.toLocaleString()}
                </div>
                <div className="text-sm text-slate-400">analyses completed</div>
              </div>
            </div>
            <div className="mt-6 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">AIO analyses</span>
                <span className="font-medium text-slate-200">{systemMetrics.activity_metrics.aio_analyses_today}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Websites analyzed</span>
                <span className="font-medium text-slate-200">{systemMetrics.activity_metrics.websites_analyzed_today}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Active users</span>
                <span className="font-medium text-purple-400">{systemMetrics.activity_metrics.active_users_today}</span>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50 shadow-xl hover:shadow-2xl hover:shadow-slate-500/10 transition-all duration-300">
            <h3 className="text-lg font-medium text-white mb-4 flex items-center">
              <svg className="w-5 h-5 mr-2 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Quick Actions
            </h3>
            <div className="space-y-3">
              <button className="w-full text-left px-4 py-3 text-sm bg-slate-700/50 hover:bg-slate-600/50 border border-slate-600/30 hover:border-slate-500/50 text-slate-300 hover:text-white rounded-lg transition-all duration-200 group flex items-center">
                <svg className="w-4 h-4 mr-3 text-slate-400 group-hover:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                View System Logs
              </button>
              <button className="w-full text-left px-4 py-3 text-sm bg-slate-700/50 hover:bg-slate-600/50 border border-slate-600/30 hover:border-slate-500/50 text-slate-300 hover:text-white rounded-lg transition-all duration-200 group flex items-center">
                <svg className="w-4 h-4 mr-3 text-slate-400 group-hover:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export Metrics
              </button>
              <button className="w-full text-left px-4 py-3 text-sm bg-slate-700/50 hover:bg-slate-600/50 border border-slate-600/30 hover:border-slate-500/50 text-slate-300 hover:text-white rounded-lg transition-all duration-200 group flex items-center">
                <svg className="w-4 h-4 mr-3 text-slate-400 group-hover:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Schedule Maintenance
              </button>
              <button className="w-full text-left px-4 py-3 text-sm bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 hover:border-red-400/50 text-red-400 hover:text-red-300 rounded-lg transition-all duration-200 group flex items-center">
                <svg className="w-4 h-4 mr-3 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                Emergency Mode
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemOverview;